// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useEffect, useCallback, useRef } from 'react';
import { agentConfig } from '../agent-config';

// Design target for the CONTENT area (viewport minus expanded sidebar ~220px).
// 1300 = content width where zoom reaches 100%. On a 2560x1600@200% display
// (effective 1280px viewport, ~1060px content), this gives ~82% zoom.
const CONTENT_DESIGN_WIDTH = 1300;
const MIN_ZOOM = 0.7;
const MAX_ZOOM = 1.0;
const ZOOM_STEP = 0.05;
const STORAGE_PREFIX = `${agentConfig.agentSlug}-agent-zoom`;

export type ZoomMode = 'auto' | 'manual';

export interface ZoomState {
  /** Current effective zoom level (0.5-1.0) */
  zoom: number;
  /** Display percentage (50-100) */
  zoomPercent: number;
  /** Whether zoom is auto-calculated or user-overridden */
  mode: ZoomMode;
  /** Increase zoom by one step */
  zoomIn: () => void;
  /** Decrease zoom by one step */
  zoomOut: () => void;
  /** Reset to auto mode */
  resetToAuto: () => void;
  /** Whether zoom-in is possible */
  canZoomIn: boolean;
  /** Whether zoom-out is possible */
  canZoomOut: boolean;
  /** Ref to attach to the content area (.app-main) for width measurement */
  contentRef: React.RefObject<HTMLElement | null>;
}

function clampZoom(value: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));
}

function computeAutoZoom(contentWidth: number): number {
  if (contentWidth >= CONTENT_DESIGN_WIDTH) return 1.0;
  if (contentWidth <= 0) return 1.0; // element not mounted yet
  return clampZoom(contentWidth / CONTENT_DESIGN_WIDTH);
}

function storageKey(userId: string | undefined): string {
  return userId ? `${STORAGE_PREFIX}-${userId}` : STORAGE_PREFIX;
}

interface StoredZoom {
  mode: ZoomMode;
  manualLevel?: number;
}

function loadStored(userId: string | undefined): StoredZoom {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) return JSON.parse(raw) as StoredZoom;
  } catch { /* ignore */ }
  return { mode: 'auto' };
}

function saveStored(userId: string | undefined, state: StoredZoom): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch { /* ignore */ }
}

export function useAutoZoom(userId?: string): ZoomState {
  const contentRef = useRef<HTMLElement | null>(null);
  const [stored, setStored] = useState<StoredZoom>(() => loadStored(userId));
  const [autoZoom, setAutoZoom] = useState(1.0);

  // Reload stored preference when userId changes (login)
  useEffect(() => {
    setStored(loadStored(userId));
  }, [userId]);

  // Observe content area width via ResizeObserver — responds to sidebar collapse,
  // window resize, and any other layout change affecting .app-main.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      // Fallback: use window.innerWidth if ref not attached
      const fallback = () => { setAutoZoom(computeAutoZoom(window.innerWidth - 220)); };
      fallback();
      window.addEventListener('resize', fallback);
      return () => { window.removeEventListener('resize', fallback); };
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
        setAutoZoom(computeAutoZoom(width));
      }
    });
    observer.observe(el);
    // Initial measurement
    setAutoZoom(computeAutoZoom(el.clientWidth));
    return () => { observer.disconnect(); };
  }, []);

  const zoom = stored.mode === 'manual' && stored.manualLevel != null
    ? clampZoom(stored.manualLevel)
    : autoZoom;

  const currentAutoZoom = useCallback(() => {
    const el = contentRef.current;
    const width = el ? el.clientWidth : window.innerWidth - 220;
    return computeAutoZoom(width);
  }, []);

  const zoomIn = useCallback(() => {
    const current = stored.mode === 'manual' && stored.manualLevel != null
      ? stored.manualLevel : currentAutoZoom();
    const next = clampZoom(current + ZOOM_STEP);
    const state: StoredZoom = { mode: 'manual', manualLevel: next };
    setStored(state);
    saveStored(userId, state);
  }, [stored, userId, currentAutoZoom]);

  const zoomOut = useCallback(() => {
    const current = stored.mode === 'manual' && stored.manualLevel != null
      ? stored.manualLevel : currentAutoZoom();
    const next = clampZoom(current - ZOOM_STEP);
    const state: StoredZoom = { mode: 'manual', manualLevel: next };
    setStored(state);
    saveStored(userId, state);
  }, [stored, userId, currentAutoZoom]);

  const resetToAuto = useCallback(() => {
    const state: StoredZoom = { mode: 'auto' };
    setStored(state);
    saveStored(userId, state);
  }, [userId]);

  return {
    zoom,
    zoomPercent: Math.round(zoom * 100),
    mode: stored.mode,
    zoomIn,
    zoomOut,
    resetToAuto,
    canZoomIn: zoom < MAX_ZOOM,
    canZoomOut: zoom > MIN_ZOOM,
    contentRef,
  };
}
